/**
 * The sound of any file sync by sound can read, by what its first bytes say
 * it is (never its name): MP4/MOV/M4A (isoAudio), WAV and RF64/BW64,
 * AIFF/AIFC (pcmFiles), MP3 (mp3Audio), raw AAC (adtsAudio) and WebM/MKV
 * (matroskaAudio).
 *
 * MP4, MOV and the PCM files have an index, read whole when opened. MP3, raw
 * AAC and WebM/MKV have none, so opening reads only their header and each
 * stretch is found when it is read (through a block cache, or with its
 * frames kept as they are found, so finding it and decoding it cost one
 * download).
 */

import type { ByteSource } from './byteSource';
import { PcmClip, ReadAudioOptions, readAudioClip } from './audioDecode';
import { cachedByteSource } from './cachedByteSource';
import { AudioTrack, PacketTable, readAudioTrack } from './isoAudio';
import {
  MatroskaTrack,
  XiphAudioTrack,
  frameBackedSource,
  isMatroskaHeader,
  isMatroskaTrack,
  isXiphTrack,
  readMatroskaTrack,
} from './matroskaAudio';
import { Mp3AudioTrack, isMp3Header, isMp3Track, readMp3Track } from './mp3Audio';
import { AdtsAudioTrack, isAdtsHeader, isAdtsTrack, readAdtsTrack, startsWithAdtsAfterId3 } from './adtsAudio';
import { isAiffHeader, isWavHeader, readAiffTrack, readWavTrack } from './pcmFiles';
import { PacketDecoder, readXiphClip } from './xiphDecode';

export type MediaFileKind = 'wav' | 'aiff' | 'mp3' | 'adts' | 'matroska' | 'iso';

export type MediaAudioTrack = AudioTrack | XiphAudioTrack | Mp3AudioTrack | AdtsAudioTrack | MatroskaTrack;

export interface OpenedMediaAudio {
  kind: MediaFileKind;
  /** The source to read stretches through (cached for MP3 and WebM/MKV). */
  source: ByteSource;
  track: MediaAudioTrack;
}

export interface OpenMediaOptions {
  signal?: AbortSignal;
  /** Fragmented MP4: stop indexing past this many seconds. */
  maxSeconds?: number;
}

export interface ClipOptions extends ReadAudioOptions {
  packetDecoder?: PacketDecoder;
  /** Give up rather than download more than this to find a stretch of an MP3 or WebM/MKV. */
  maxScanBytes?: number;
}

/** What kind of file the first bytes belong to. */
export const sniffMediaFile = (head: Uint8Array): MediaFileKind => {
  if (isWavHeader(head)) return 'wav';
  if (isAiffHeader(head)) return 'aiff';
  if (isMatroskaHeader(head)) return 'matroska';
  if (isAdtsHeader(head)) return 'adts';
  if (isMp3Header(head)) return 'mp3';
  return 'iso';
};

export const openMediaAudio = async (source: ByteSource, options: OpenMediaOptions = {}): Promise<OpenedMediaAudio> => {
  const head = await source.read(0, 16, options.signal);
  let kind = sniffMediaFile(head);
  // An ID3 tag can lead an AAC stream as well as an MP3.
  if (kind === 'mp3' && head[0] === 0x49 && (await startsWithAdtsAfterId3(source, options.signal))) kind = 'adts';
  switch (kind) {
    case 'wav':
      return { kind, source, track: await readWavTrack(source, options.signal) };
    case 'aiff':
      return { kind, source, track: await readAiffTrack(source, options.signal) };
    case 'mp3': {
      const cached = cachedByteSource(source);
      return { kind, source: cached, track: await readMp3Track(cached, { signal: options.signal, fromS: 0, toS: 0 }) };
    }
    case 'adts': {
      const cached = cachedByteSource(source);
      return { kind, source: cached, track: await readAdtsTrack(cached, { signal: options.signal, fromS: 0, toS: 0 }) };
    }
    case 'matroska': {
      const cached = cachedByteSource(source);
      return { kind, source: cached, track: await readMatroskaTrack(cached, { signal: options.signal }) };
    }
    default:
      return { kind, source, track: await readAudioTrack(source, { signal: options.signal, maxSeconds: options.maxSeconds }) };
  }
};

/** A byte source that reports how much of an expected download it has read. */
const progressSource = (source: ByteSource, expectedBytes: number, report: (fraction: number) => void): ByteSource => {
  let read = 0;
  return {
    size: source.size,
    async read(offset, length, signal) {
      const bytes = await source.read(offset, length, signal);
      read += bytes.length;
      if (expectedBytes > 0) report(Math.min(1, read / expectedBytes));
      return bytes;
    },
  };
};

/**
 * Mono samples for presentation seconds [startS, endS) of an opened file's
 * sound, at `sampleRate`.
 */
export const readTrackClip = async (
  source: ByteSource,
  track: MediaAudioTrack,
  startS: number,
  endS: number,
  sampleRate: number,
  options: ClipOptions = {}
): Promise<PcmClip> => {
  let stretch: MediaAudioTrack = track;
  let reader = source;
  if (isMp3Track(track as AudioTrack) || isAdtsTrack(track as AudioTrack) || isMatroskaTrack(track)) {
    // Finding the stretch is most of the work for these: report it as such.
    const duration = track.durationSeconds > 0 ? track.durationSeconds : 0;
    const expected = duration > 0 ? (source.size * Math.max(0, Math.min(endS, duration) - startS)) / duration : 0;
    const finding = progressSource(source, expected, (fraction) => options.onProgress?.(fraction * 0.6));
    if (isMatroskaTrack(track)) {
      // The audio frames are kept as the clusters are walked: reading them
      // again would read the video around them too.
      const found = await readMatroskaTrack(finding, {
        fromS: startS,
        toS: endS,
        layout: track.matroska,
        signal: options.signal,
        maxScanBytes: options.maxScanBytes,
        collectFrames: true,
      });
      stretch = found;
      if (found.frames) reader = frameBackedSource(source, found.table as PacketTable, found.frames);
    } else if (isAdtsTrack(track as AudioTrack)) {
      const adts = track as AdtsAudioTrack;
      stretch = await readAdtsTrack(finding, {
        fromS: startS,
        toS: endS,
        layout: adts.adts,
        index: adts.index,
        signal: options.signal,
        maxScanBytes: options.maxScanBytes,
      });
    } else {
      stretch = await readMp3Track(finding, {
        fromS: startS,
        toS: endS,
        layout: (track as Mp3AudioTrack).mp3,
        index: (track as Mp3AudioTrack).index,
        signal: options.signal,
        maxScanBytes: options.maxScanBytes,
      });
    }
    const report = options.onProgress;
    options = { ...options, onProgress: report ? (fraction: number) => report(0.6 + fraction * 0.4) : undefined };
  }
  if (isXiphTrack(stretch)) return readXiphClip(reader, stretch, startS, endS, sampleRate, options);
  return readAudioClip(reader, stretch as AudioTrack, startS, endS, sampleRate, options);
};
